/**
 * CTO Links directory (task 064-01) — Chelsie's QA-07 / Slack message: "I
 * need to view all CTO links for a traffic source. Currently, they're
 * organized at the promo level. I'm open to a simple solution, such as a
 * traffic-source filter or search." Her "simple solution" line sets the
 * ceiling (task file): a filtered list, not a report — the CTO report at
 * `/reports/cto` already owns stats and is untouched.
 *
 * Every `cto_email` campaign's link rows, one flat table, read-only. A
 * traffic-source `<Select>` re-fetches from the api (`siteId` narrows
 * server-side); free-text search then narrows further over the rows already
 * loaded (client-side, same idiom as the Promo Types screen). A link with
 * zero leads still appears — the gap the CTO report leaves (no leads ∪
 * rejections join here at all).
 *
 * HF-139 (QA-37): the campaign name links to that exact row in the form's CTO
 * links section (`#cto-link-<id>`), which scrolls to it and rings it briefly.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { SiteResponse } from '@leads-platform/shared';
import { api, ApiError, type CampaignLinkDirectoryRow } from '../lib/api';
import { fmtMoney } from '../lib/format';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ctoLinkAnchorId } from '../components/cto-links-section';
import { SearchInput } from '../components/ui/search-input';
import { Select } from '../components/ui/select';
import { StatusPill } from '../components/ui/status-pill';
import { CopyButton } from '../components/ui/copy-button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

/** Search over campaign name, promo type, traffic source label and the URL. */
function searchBlob(row: CampaignLinkDirectoryRow): string {
  return [row.campaignName, row.promoName, row.siteLabel, row.url]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/**
 * A→Z by label, case-insensitively, tie-broken by numeric site id ascending
 * (HF-081's comparator, `screens/campaigns.tsx:522-523`). Driver: Chelsie's
 * QA-07, 9/26 note — "Change traffic source drop down so it's alphabetical."
 */
function bySiteLabel(a: SiteResponse, b: SiteResponse): number {
  const cmp = a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  return cmp !== 0 ? cmp : Number(a.id) - Number(b.id);
}

export function CtoLinks() {
  const [rows, setRows] = useState<CampaignLinkDirectoryRow[]>([]);
  const [sites, setSites] = useState<SiteResponse[]>([]);
  const [siteId, setSiteId] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadRows = useCallback((siteFilter: string) => {
    setLoading(true);
    setError(null);
    api
      .getCampaignLinkDirectory(siteFilter || undefined)
      .then(setRows)
      .catch((err: ApiError) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    api
      .getSites()
      .then((fetched) => setSites([...fetched].sort(bySiteLabel)))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    loadRows(siteId);
  }, [siteId, loadRows]);

  const filteredRows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => searchBlob(row).includes(needle));
  }, [rows, q]);

  if (error) {
    return (
      <div>
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          Error loading CTO links: {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">CTO Links</h1>
          <p className="text-muted-foreground mt-2">
            Every CTO link across every campaign, read-only. Filter by traffic source or search —
            edits stay on the campaign form.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle>All CTO Links</CardTitle>
            <div className="flex flex-wrap items-center gap-3">
              <Select
                aria-label="Traffic source"
                value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
                className="h-9 min-w-[220px]"
              >
                <option value="">All traffic sources</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.label}
                  </option>
                ))}
              </Select>
              <SearchInput
                value={q}
                onChange={setQ}
                placeholder="Search campaign, promo, source, URL"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Traffic source</TableHead>
                <TableHead>Promo type</TableHead>
                <TableHead>Campaign</TableHead>
                <TableHead>Link status</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>URL</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    {rows.length === 0
                      ? 'No CTO links found.'
                      : 'No links match your filter or search.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.siteLabel}</TableCell>
                    <TableCell>{row.promoName}</TableCell>
                    <TableCell>
                      <Link
                        to={`/campaigns/${row.campaignId}#${ctoLinkAnchorId(row.id)}`}
                        className="font-semibold text-ink hover:text-accent transition-colors"
                      >
                        {row.campaignName}
                      </Link>
                      <div className="mt-1">
                        <StatusPill value={row.campaignStatus} />
                      </div>
                    </TableCell>
                    <TableCell className="capitalize text-muted-foreground">{row.status}</TableCell>
                    <TableCell>{row.price != null ? fmtMoney(row.price) : '—'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span
                          className="max-w-[280px] truncate font-mono text-xs text-muted-foreground"
                          title={row.url}
                        >
                          {row.url}
                        </span>
                        <CopyButton value={row.url} title="Copy the CTO link" />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
